import { useRive } from "@rive-app/react-canvas";

import type { PhonemeSpan } from "../api/transcription";
import styles from "../styles/VisemeAnimation.module.scss";

import { AudioControlButtons } from "./AudioControlButtons";
import { useVisemeAnimator } from "./useVisemeAnimator";

/**
 * VisemeAnimation is used to preview viseme animations.
 *
 * @param props is described below.
 * @param props.artboardName is the name of the artboard to be used contained in the rig.
 * @param props.audioRef is a reference to the audio that gets created by this component. It can be used to get the current state of the audio, or to control the audio.
 * @param props.audioSource is the url of the audio to play.
 * @param props.phonemeSpans is used to decide which viseme to show at a given time.
 * @param props.visemeRigSrcOrBuffer is has one of src or buffer, described below.
 * @param props.visemeRigSrcOrBuffer.src is a url to the rig to use for visemes.
 * @param props.visemeRigSrcOrBuffer.buffer is the raw data for the rig to use for visemes.
 * @param props.stateMachineName is the state machine to be used contained in the rig.
 */
export const VisemeAnimation = ({
  artboardName,
  audioRef,
  audioSource,
  phonemeSpans,
  visemeRigSrcOrBuffer,
  stateMachineName,
}: {
  artboardName: string;
  audioRef: React.MutableRefObject<HTMLAudioElement | undefined>;
  audioSource: string;
  phonemeSpans: PhonemeSpan[];
  visemeRigSrcOrBuffer?: { src?: string; buffer?: ArrayBuffer };
  stateMachineName: string;
}) => {
  const { rive, canvas, RiveComponent } = useRive(
    {
      artboard: artboardName,
      autoplay: true,
      stateMachines: stateMachineName,
      ...visemeRigSrcOrBuffer,
    },
    {
      fitCanvasToArtboardHeight: true,
      useDevicePixelRatio: true,
      useOffscreenRenderer: false,
    },
  );

  const { animate, stop } = useVisemeAnimator(
    rive,
    phonemeSpans,
    stateMachineName,
  );

  return (
    <div className={styles["display-box"]}>
      <div className={styles["viseme-rive-container"]}>
        {RiveComponent ? <RiveComponent /> : null}
      </div>
      <AudioControlButtons
        audioRef={audioRef}
        canvas={canvas}
        color="macaw"
        onAudioPause={stop}
        onAudioPlay={time => animate(time)}
        onAudioScrub={time => {
          stop();
          if (audioRef.current && !audioRef.current.paused) {
            animate(time);
          }
        }}
        source={audioSource}
        submitting={false}
        type="submit"
        variant="solid"
      />
    </div>
  );
};
